import { getModuleType } from 'EmailData/constants/DefaultModulesConstants';
import { AB_SELECT_VALUE } from 'EmailData/constants/details/abEmailConstants';
import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { WarningIcon } from 'foundations-assets';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { Small } from 'foundations-components/transitional/components/Small';
import { useCallback, useMemo } from 'react';
import useEmailCloneCustomPropertiesState from '../hooks/useEmailCloneCustomPropertiesState';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export default function EmailCloneSettings({
  isAbEmail,
  selectedABVersion,
  smartContentLocations = [],
  variantSmartContentLocations = [],
  variantHasCustomPlaintext = false,
  hasCustomPlaintext = false,
  onSelectPlaintext,
  onSelectSmartContent,
  onSelectPermissions,
  cloneWithSmartContent,
  cloneWithCustomPlaintext,
  retainPermissionsAfterClone,
  teamName,
  teamCount,
  userCount,
  assignedCustomProperties
}) {
  const {
    shouldCloneCustomProperties,
    setShouldCloneCustomProperties
  } = useEmailCloneCustomPropertiesState();
  const parsedSmartContentLocations = useCallback(contentLocations => {
    const locations = contentLocations.reduce((acc, location) => {
      let key;
      if (location.isModule) {
        key = getModuleType(location.moduleId) || 'customModule';
      } else {
        key = location.locationName;
      }
      if (acc[key]) {
        acc[key]++;
      } else {
        acc[key] = 1;
      }
      return acc;
    }, {});
    return Object.keys(locations).map(type => {
      return renderSmartContentText(locations, type);
    }).join(', ');
  }, []);
  const onCheckCustomProperties = useCallback(e => {
    setShouldCloneCustomProperties(e.target.checked);
  }, [setShouldCloneCustomProperties]);
  const onCheckPermissions = useCallback(e => {
    onSelectPermissions(e.target.checked);
  }, [onSelectPermissions]);
  const onCheckSmartContent = useCallback(e => {
    onSelectSmartContent(e.target.checked);
  }, [onSelectSmartContent]);
  const handleCheckPlaintext = useCallback(e => {
    onSelectPlaintext(e.target.checked);
  }, [onSelectPlaintext]);
  const invalidSmartContentWarning = useMemo(() => {
    var _dataToUse$filter;
    const dataToUse = isAbEmail ? variantSmartContentLocations : smartContentLocations;
    const invalidLocations = (_dataToUse$filter = dataToUse === null || dataToUse === void 0 ? void 0 : dataToUse.filter(location => {
      return location.isValid === false;
    })) !== null && _dataToUse$filter !== void 0 ? _dataToUse$filter : [];
    const invalidSmartContentLocations = invalidLocations.length > 0 ? invalidLocations : null;
    if (!invalidSmartContentLocations) return null;
    return /*#__PURE__*/_jsx(Alert, {
      type: "warning",
      marginBlockStart: "400",
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        children: [/*#__PURE__*/_jsx(View, {
          marginInlineEnd: "400",
          children: /*#__PURE__*/_jsx(WarningIcon, {
            color: "do-not-use-marigold",
            size: "300"
          })
        }), /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: `email-management.emailClone.cloneSidePanel.settings.smartContent.descriptionInvalidSmartContent`,
          options: {
            firstModules: parsedSmartContentLocations(invalidSmartContentLocations.length === 1 ? invalidSmartContentLocations : invalidSmartContentLocations.slice(0, -1)),
            lastModule: parsedSmartContentLocations(invalidSmartContentLocations.slice(-1)),
            count: invalidSmartContentLocations.length
          }
        })]
      })
    });
  }, [isAbEmail, parsedSmartContentLocations, smartContentLocations, variantSmartContentLocations]);
  const copyPermissionsDescription = useMemo(() => {
    let description = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "email-management.emailClone.cloneSidePanel.settings.userPermissions.all"
    });
    if (teamCount > 1 && userCount > 1) {
      description = /*#__PURE__*/_jsx(FormattedMessage, {
        message: "email-management.emailClone.cloneSidePanel.settings.userPermissions.multipleTeamsAndMultipleUsers",
        options: {
          teamCount,
          userCount
        }
      });
    } else if (teamCount === 1 && userCount > 1) {
      description = /*#__PURE__*/_jsx(FormattedMessage, {
        message: "email-management.emailClone.cloneSidePanel.settings.userPermissions.oneTeamAndMultipleUsers",
        options: {
          userCount
        }
      });
    } else if (teamCount > 1 && userCount === 1) {
      description = /*#__PURE__*/_jsx(FormattedMessage, {
        message: "email-management.emailClone.cloneSidePanel.settings.userPermissions.multipleTeamsAndOneUser",
        options: {
          teamCount
        }
      });
    } else if (teamCount === 1 && userCount === 1) {
      description = /*#__PURE__*/_jsx(FormattedMessage, {
        message: "email-management.emailClone.cloneSidePanel.settings.userPermissions.oneTeamAndOneUser"
      });
    } else if (teamCount > 1) {
      description = /*#__PURE__*/_jsx(FormattedMessage, {
        message: "email-management.emailClone.cloneSidePanel.settings.userPermissions.teams",
        options: {
          teamCount
        }
      });
    } else if (teamCount === 1 && teamName) {
      description = /*#__PURE__*/_jsx(FormattedMessage, {
        message: "email-management.emailClone.cloneSidePanel.settings.userPermissions.oneTeam",
        options: {
          teamName
        }
      });
    }
    return description;
  }, [teamCount, teamName, userCount]);
  const smartContentLangKey = useMemo(() => {
    if (isAbEmail && selectedABVersion !== AB_SELECT_VALUE.AB) {
      if (selectedABVersion === AB_SELECT_VALUE.A) {
        return smartContentLocations.length > 1 ? 'descriptionAWithAnd' : 'descriptionA';
      } else if (selectedABVersion === AB_SELECT_VALUE.B) {
        return smartContentLocations.length > 1 ? 'descriptionBWithAnd' : 'descriptionB';
      }
    } else {
      return smartContentLocations.length > 1 ? 'descriptionWithAnd' : 'description';
    }
  }, [isAbEmail, selectedABVersion, smartContentLocations.length]);
  const smartContentDescriptions = useMemo(() => {
    const versionADescription = /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: `email-management.emailClone.cloneSidePanel.settings.smartContent.${smartContentLocations.length > 1 ? 'descriptionAWithAnd' : 'descriptionA'}`,
      options: {
        multiContents: parsedSmartContentLocations(smartContentLocations.slice(0, -1)),
        singleContent: parsedSmartContentLocations(smartContentLocations.slice(-1))
      }
    });
    const versionBDescription = /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: `email-management.emailClone.cloneSidePanel.settings.smartContent.${variantSmartContentLocations.length > 1 ? 'descriptionBWithAnd' : 'descriptionB'}`,
      options: {
        multiContents: parsedSmartContentLocations(variantSmartContentLocations.slice(0, -1)),
        singleContent: parsedSmartContentLocations(variantSmartContentLocations.slice(-1))
      }
    });
    if (selectedABVersion === AB_SELECT_VALUE.AB && smartContentLocations.length > 0 && variantSmartContentLocations.length > 0) {
      return /*#__PURE__*/_jsxs(List, {
        styled: true,
        marginBlockEnd: "0",
        paddingInlineStart: "400",
        children: [/*#__PURE__*/_jsx(ListItem, {
          children: versionADescription
        }), /*#__PURE__*/_jsx(ListItem, {
          children: versionBDescription
        })]
      });
    } else if (selectedABVersion === AB_SELECT_VALUE.AB && smartContentLocations.length > 0) {
      return versionADescription;
    } else if (selectedABVersion === AB_SELECT_VALUE.AB && variantSmartContentLocations.length > 0) {
      return versionBDescription;
    }
    const locations = selectedABVersion === AB_SELECT_VALUE.B ? variantSmartContentLocations : smartContentLocations;
    return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: `email-management.emailClone.cloneSidePanel.settings.smartContent.${smartContentLangKey}`,
      options: {
        multiContents: parsedSmartContentLocations(locations.slice(0, -1)),
        singleContent: parsedSmartContentLocations(locations.slice(-1))
      }
    });
  }, [parsedSmartContentLocations, selectedABVersion, smartContentLangKey, smartContentLocations, variantSmartContentLocations]);
  const customPlainTextDescription = useMemo(() => {
    if (isAbEmail && selectedABVersion === AB_SELECT_VALUE.AB && hasCustomPlaintext && variantHasCustomPlaintext) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "email-management.emailClone.cloneSidePanel.settings.plainText.descriptionABLater"
      });
    } else if (isAbEmail && selectedABVersion === AB_SELECT_VALUE.AB && hasCustomPlaintext) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "email-management.emailClone.cloneSidePanel.settings.plainText.descriptionALater"
      });
    } else if (isAbEmail && selectedABVersion === AB_SELECT_VALUE.AB && variantHasCustomPlaintext) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "email-management.emailClone.cloneSidePanel.settings.plainText.descriptionBLater"
      });
    } else if (selectedABVersion === AB_SELECT_VALUE.A && hasCustomPlaintext) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "email-management.emailClone.cloneSidePanel.settings.plainText.descriptionA"
      });
    } else if (selectedABVersion === AB_SELECT_VALUE.B && variantHasCustomPlaintext) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "email-management.emailClone.cloneSidePanel.settings.plainText.descriptionB"
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "email-management.emailClone.cloneSidePanel.settings.plainText.description"
    });
  }, [hasCustomPlaintext, isAbEmail, selectedABVersion, variantHasCustomPlaintext]);
  const shouldRenderSmartContentCheckbox = useMemo(() => {
    if (!isAbEmail) {
      return smartContentLocations.length > 0;
    } else {
      if (selectedABVersion === AB_SELECT_VALUE.AB) {
        return smartContentLocations.length > 0 || variantSmartContentLocations.length > 0;
      } else if (selectedABVersion === AB_SELECT_VALUE.A) {
        return smartContentLocations.length > 0;
      } else {
        return variantSmartContentLocations.length > 0;
      }
    }
  }, [isAbEmail, selectedABVersion, smartContentLocations.length, variantSmartContentLocations.length]);
  const shouldRenderPlainTextCheckbox = useMemo(() => {
    if (!isAbEmail) {
      return hasCustomPlaintext;
    } else {
      if (selectedABVersion === AB_SELECT_VALUE.AB) {
        return hasCustomPlaintext || variantHasCustomPlaintext;
      } else if (selectedABVersion === AB_SELECT_VALUE.A) {
        return hasCustomPlaintext;
      } else {
        return variantHasCustomPlaintext;
      }
    }
  }, [hasCustomPlaintext, isAbEmail, selectedABVersion, variantHasCustomPlaintext]);
  const shouldRenderPermissionsCheckbox = useMemo(() => {
    return teamCount !== 0 || userCount !== 0;
  }, [teamCount, userCount]);
  if (!shouldRenderSmartContentCheckbox && !shouldRenderPlainTextCheckbox && !shouldRenderPermissionsCheckbox && !assignedCustomProperties.length) {
    return null;
  }
  return /*#__PURE__*/_jsx(FormControl, {
    marginBlockStart: "200",
    "data-test-id": "email-clone-settings",
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "email-management.emailClone.cloneSidePanel.settings.title"
    }),
    tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "email-management.emailClone.cloneSidePanel.settings.tooltip"
    }),
    tooltipPlacement: "right",
    children: /*#__PURE__*/_jsxs(_Fragment, {
      children: [assignedCustomProperties.length > 0 && /*#__PURE__*/_jsx(Checkbox, {
        onChange: onCheckCustomProperties,
        description: /*#__PURE__*/_jsx(Small, {
          use: "help",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "email-management.emailClone.cloneSidePanel.settings.customProperties.description",
            options: {
              count: assignedCustomProperties.length
            }
          })
        }),
        checked: shouldCloneCustomProperties,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "email-management.emailClone.cloneSidePanel.settings.customProperties.label"
        })
      }), shouldRenderPermissionsCheckbox && /*#__PURE__*/_jsx(Checkbox, {
        onChange: onCheckPermissions,
        description: /*#__PURE__*/_jsx(Small, {
          use: "help",
          children: copyPermissionsDescription
        }),
        checked: retainPermissionsAfterClone,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "email-management.emailClone.cloneSidePanel.settings.userPermissions.label"
        })
      }), shouldRenderSmartContentCheckbox && /*#__PURE__*/_jsxs(View, {
        marginBlockStart: "200",
        children: [/*#__PURE__*/_jsx(Checkbox, {
          onChange: onCheckSmartContent,
          description: /*#__PURE__*/_jsx(Small, {
            use: "help",
            children: smartContentDescriptions
          }),
          checked: cloneWithSmartContent,
          disabled: selectedABVersion === AB_SELECT_VALUE.AB || invalidSmartContentWarning !== null,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "email-management.emailClone.cloneSidePanel.settings.smartContent.label"
          })
        }), invalidSmartContentWarning]
      }), shouldRenderPlainTextCheckbox && /*#__PURE__*/_jsx(View, {
        marginBlockStart: "200",
        children: /*#__PURE__*/_jsx(Checkbox, {
          onChange: handleCheckPlaintext,
          description: /*#__PURE__*/_jsx(Small, {
            use: "help",
            children: customPlainTextDescription
          }),
          checked: cloneWithCustomPlaintext,
          disabled: selectedABVersion === AB_SELECT_VALUE.AB,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "email-management.emailClone.cloneSidePanel.settings.plainText.label"
          })
        })
      })]
    })
  });
}
function renderSmartContentText(locations, type) {
  if (type === 'SUBJECT') {
    return I18n.text('email-management.emailClone.cloneSidePanel.customContent.location.SUBJECT.subjectLine');
  }
  return I18n.text(`email-management.emailClone.cloneSidePanel.customContent.location.${type}`, {
    count: locations[type]
  });
}